import { computed } from "vue";
import { useRoute } from "vue-router";
import { assistantPageState, type PageFilterValue } from "@/composables/useAssistantPageState";

export interface AssistantScope {
  /** Registry key, matching Assistant::Scopes on the API side. */
  key: string;
  contextType: "survey" | "report" | "ad_template" | null;
  contextToken: string | null;
  pageFilters: Record<string, PageFilterValue>;
}

interface AssistantRouteMeta {
  scope: string;
  /** Panes of a page that carry their own scope, keyed by URL hash. */
  byHash?: Record<string, string>;
  contextType?: "survey" | "report" | "ad_template";
  /** Route param holding the context token, when it is not `token`. */
  contextParam?: string;
}

const NOT_FILTERS = ["mode", "layout", "page", "per_page", "tab", "sort", "order", "Dashboard", "card"];

export function countActiveFilters(pageFilters: Record<string, PageFilterValue>): number {
  return Object.entries(pageFilters)
    .filter(([key]) => !NOT_FILTERS.includes(key))
    .reduce((count, [, value]) => count + (Array.isArray(value) ? value.length : 1), 0);
}

// Declared as `meta.assistant` on the route; the API re-resolves and authorizes everything sent here.
export function useAssistantScope() {
  const route = useRoute();

  const meta = computed<AssistantRouteMeta | null>(() => (route.meta.assistant as AssistantRouteMeta) ?? null);

  const scope = computed<AssistantScope | null>(() => {
    if (!meta.value) return null;

    const key = meta.value.byHash?.[route.hash] ?? meta.value.scope;
    const contextType = meta.value.contextType ?? null;
    const contextParam = meta.value.contextParam ?? "token";
    const contextToken = contextType ? ((route.params[contextParam] as string) ?? null) : null;
    if (contextType && !contextToken) return null;

    return { key, contextType, contextToken, pageFilters: pageFilters() };
  });

  // The URL is the page's filter state, so the assistant answers about the
  // slice the user is looking at. The API drops the keys that are not filters.
  function pageFilters() {
    const fromUrl = Object.entries(route.query)
      .filter(([, value]) => value != null && value !== "")
      .map(([key, value]) => [key, Array.isArray(value) ? value.join(",") : String(value)]);
    return { ...Object.fromEntries(fromUrl), ...assistantPageState.value };
  }

  return { scope };
}

export default useAssistantScope;
